import { styled } from '@linaria/react';
import { ButtonProps, Button as MantineButton, PolymorphicComponentProps } from '@mantine/core';
import React, { CSSProperties } from 'react';
import {
  getBackgroundColor,
  getBackgroundColorHover,
  getBorderRadius,
  getColor,
  getColorHover,
  getPadding,
} from './button.helpers';

type Props = PolymorphicComponentProps<'button', ButtonProps> & {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'warning';
  noAnimation?: boolean;
  style?: CSSProperties;
};

export const Button = React.forwardRef<HTMLButtonElement, Props>(
  ({ size = 'md', radius = 'md', variant = 'primary', style, noAnimation = true, ...props }: Props, ref) => {
    const styles = noAnimation
      ? {
          root: { transition: 'none', animation: 'none', transform: 'none' },
          inner: { transition: 'none', animation: 'none' },
        }
      : undefined;

    return (
      <StyledButton ref={ref} size={size} radius={radius} variant={variant} style={style} styles={styles} {...props} />
    );
  },
);

const StyledButton = styled(MantineButton)<Props>`
  background-color: ${({ variant }) => getBackgroundColor(variant)};
  border-radius: ${({ radius }) => getBorderRadius(radius)};
  border: ${({ variant }) => (variant === 'outline' ? '1px solid var(--sky-base)' : 'none')};
  padding: ${({ size }) => getPadding(size)};
  color: ${({ variant }) => getColor(variant)};
  cursor: pointer;

  .material-icons,
  .material-icons-outlined {
    line-height: 0;
    cursor: pointer;
  }

  .mantine-Button-label {
    line-height: 0;
  }

  &:hover,
  &:active {
    background-color: ${({ variant }) => getBackgroundColorHover(variant)};
    border: ${({ variant }) => (variant === 'outline' ? '1px solid var(--secondary-base)' : 'none')};
    color: ${({ variant }) => getColorHover(variant)};
  }

  &:disabled {
    background-color: var(--sky-lighter);
    color: var(--sky-dark);
    border: none;
  }

  &[data-loading] {
    background-color: ${({ variant }) => getBackgroundColor(variant)};
    opacity: 0.7;
    
    &:hover {
      background-color: ${({ variant }) => getBackgroundColorHover(variant)};
    }

    .mantine-Button-loader {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: auto;
      
      /* Target the inner loader root element */
      .mantine-Loader-root {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        
        /* Fix the ::after pseudo-element positioning */
        &::after {
          position: absolute;
          top: -20%;
          margin: 0;
        }
      }
    }
  }
`;
